import {
  AUSTRIA_INTERNATIONAL_RATES,
  GERMANY_INTERNATIONAL_RATES,
  NETHERLANDS_INTERNATIONAL_RATES,
  UK_INTERNATIONAL_CITY_RATES,
} from './constants'
import type {BreakdownItem, CalcResult, DayLog, Destination, MealsState} from './types'

// ── Date helpers ─────────────────────────────────────────────────

export const toISOLocal = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0')

  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

export const dayLabel = (d: Date): string => d.toLocaleDateString('en-GB', {day: 'numeric', month: 'short'})

export const fmt = (value: number, currencySymbol: string): string => `${currencySymbol}${Math.abs(value).toFixed(2)}`

export const getDefaultDates = (): {departure: string; returnDate: string} => {
  const dep = new Date()
  dep.setDate(dep.getDate() + 1)
  dep.setHours(8, 30, 0, 0)
  const ret = new Date(dep)
  ret.setDate(ret.getDate() + 3)
  ret.setHours(17, 0, 0, 0)

  return {departure: toISOLocal(dep), returnDate: toISOLocal(ret)}
}

// ── UK calculator ────────────────────────────────────────────────

export const calcUK = (
  dep: Date,
  ret: Date,
  destMode: Destination,
  intlDest: string,
  meals: MealsState,
): CalcResult => {
  if (destMode === 'international') {
    if (!intlDest) {
      return {currency: '£', total: 0, items: [], ruleInfo: 'Select a destination to calculate.', meta: ''}
    }

    if (intlDest === 'United Kingdom' || intlDest === 'United Kingdom - London') {
      return calcUK(dep, ret, 'domestic', '', meals)
    }

    const rate = UK_INTERNATIONAL_CITY_RATES[intlDest]

    if (!rate) {
      return {currency: '£', total: 0, items: [], ruleInfo: 'Destination not found in HMRC table.', meta: ''}
    }

    const hrs = (ret.getTime() - dep.getTime()) / 3_600_000
    const nights = Math.max(0, Math.floor((ret.getTime() - dep.getTime()) / 86_400_000))
    const full24h = Math.floor(hrs / 24)
    const leftover = hrs - full24h * 24
    let residualRate = 0
    let residualLabel = ''

    if (leftover >= 10) {
      residualRate = rate.rate10h
      residualLabel = 'Over 10h residual'
    } else if (leftover >= 5) {
      residualRate = rate.rate5h
      residualLabel = 'Over 5h residual'
    }

    const subtotal = full24h * rate.rate24h + residualRate
    const periods = Math.max(1, full24h + (residualRate > 0 ? 1 : 0))

    const perPeriodDed =
      (meals.breakfast ? rate.breakfast : 0) + (meals.lunch ? rate.lunch : 0) + (meals.dinner ? rate.dinner : 0)

    const totalDed = Math.min(perPeriodDed * periods, subtotal)
    const total = Math.max(0, subtotal - totalDed)
    const sym = rate.symbol

    const items: BreakdownItem[] = []

    if (full24h > 0) {
      items.push({
        label: 'Subsistence (24h)',
        description: `${full24h} × ${sym}${rate.rate24h}`,
        value: full24h * rate.rate24h,
        type: 'pos',
      })
    }

    if (residualRate > 0) {
      items.push({
        label: residualLabel,
        description: `${sym}${residualRate}`,
        value: residualRate,
        type: 'pos',
      })
    }

    if (totalDed > 0) {
      const providedLabels: string[] = []

      if (meals.breakfast) {
        providedLabels.push(`breakfast ${rate.breakfast === 0 ? '(in room)' : sym + rate.breakfast}`)
      }

      if (meals.lunch) {
        providedLabels.push(`lunch ${sym}${rate.lunch}`)
      }

      if (meals.dinner) {
        providedLabels.push(`dinner ${sym}${rate.dinner}`)
      }

      items.push({
        label: 'Meal deductions',
        description: providedLabels.join(', '),
        value: -totalDed,
        type: 'neg',
      })
    }

    items.push({
      label: 'Accommodation',
      description: `Actual cost (HMRC room guide: ${sym}${rate.room})`,
      value: 0,
      type: 'zero',
    })

    items.push({
      label: 'Total subsistence',
      description: 'Tax-free (HMRC EIM05255)',
      value: total,
      type: 'tot',
      isTotal: true,
    })

    const log: DayLog[] = []
    const cur = new Date(dep)
    cur.setHours(0, 0, 0, 0)
    const last = new Date(ret)
    last.setHours(0, 0, 0, 0)

    while (cur <= last) {
      const dayEnd = new Date(cur)
      dayEnd.setHours(23, 59, 59)
      const s = cur < dep ? dep : cur
      const e = dayEnd > ret ? ret : dayEnd
      const dayHrs = Math.round(((e.getTime() - s.getTime()) / 3_600_000) * 10) / 10

      log.push({
        date: dayLabel(new Date(cur)),
        type: `${intlDest}`,
        hours: dayHrs,
        amount: 0,
        explanation: `Rates apply per 24h trip period, not per calendar day`,
      })

      cur.setDate(cur.getDate() + 1)
    }

    return {
      currency: sym,
      total,
      items,
      log,
      ruleInfo: `<strong>HMRC Worldwide Subsistence (EIM05255)</strong>: ${intlDest}. 24h rate: ${sym}${rate.rate24h}; >10h residual: ${sym}${rate.rate10h}; >5h residual: ${sym}${rate.rate5h}. Trip aggregated as full-24h periods plus a residual for the remainder. Provided meals deducted at per-meal rates (breakfast often ${sym}0 because included in room). Accommodation reimbursed at actual cost (HMRC room rate shown for guidance).`,
      meta: `${intlDest} · ${Math.round(hrs)}h · ${nights} overnight${nights === 1 ? '' : 's'}`,
      rateNote: `HMRC EIM05255: ${intlDest} · ${sym}${rate.rate24h}/24h`,
    }
  }

  // HMRC benchmark scale rates (EIM05231) — per-day calculation
  // Rates: Breakfast £5, One meal (5h) £5, Two meal (10h) £10, Late evening £15
  // Max 3 meal rates per 24h (theoretical max £30)
  const totalHrs = (ret.getTime() - dep.getTime()) / 3_600_000
  const nights = Math.max(0, Math.floor((ret.getTime() - dep.getTime()) / 86_400_000))
  const log: DayLog[] = []
  let total = 0
  let breakfastDed = 0
  const cur = new Date(dep)
  cur.setHours(0, 0, 0, 0)
  const last = new Date(ret)
  last.setHours(0, 0, 0, 0)
  const isSingleDay = cur.toDateString() === last.toDateString()

  while (cur <= last) {
    const isFirst = cur.toDateString() === dep.toDateString()
    const isLast = cur.toDateString() === last.toDateString()
    const dayEnd = new Date(cur)
    dayEnd.setHours(23, 59, 59)
    const s = cur < dep ? dep : cur
    const e = dayEnd > ret ? ret : dayEnd
    const dayHrs = (e.getTime() - s.getTime()) / 3_600_000
    const isOvernight = !isSingleDay && !isLast
    const earlyStartDay = isFirst && dep.getHours() < 6
    const atEightPm = isOvernight || (isLast && ret.getHours() >= 20) || (isSingleDay && ret.getHours() >= 20)
    let dayTotal = 0
    // HMRC EIM05231: max 3 meal *rates* per 24h. Each of Breakfast / One-meal /
    // Two-meal / Late-evening counts as ONE rate (Two-meal is a single rate that
    // covers two meals internally, not two slots). Theoretical max: £5 + £10 + £15 = £30.
    let ratesUsed = 0
    const parts: string[] = []
    const amounts: string[] = []

    if (earlyStartDay && !meals.breakfast && ratesUsed < 3) {
      dayTotal += 5
      ratesUsed += 1
      parts.push('Breakfast')
      amounts.push('£5')
    } else if (earlyStartDay && meals.breakfast) {
      breakfastDed += 5
    }

    const lateEveningEligible = atEightPm && (dayHrs >= 15 || isOvernight)

    if (dayHrs >= 10 && ratesUsed < 3) {
      dayTotal += 10
      ratesUsed += 1
      parts.push('Two meal')
      amounts.push('£10')
    } else if (dayHrs >= 5 && ratesUsed < 3) {
      dayTotal += 5
      ratesUsed += 1
      parts.push('One meal')
      amounts.push('£5')
    }

    if (lateEveningEligible && ratesUsed < 3) {
      dayTotal += 15
      ratesUsed += 1
      parts.push('Late evening')
      amounts.push('£15')
    }

    total += dayTotal

    let explanation: string

    if (amounts.length > 1) {
      explanation = `${amounts.join(' + ')} = £${dayTotal.toFixed(2)} · ${parts.join(' + ')}`
    } else if (amounts.length === 1) {
      explanation = `${amounts[0]} · ${parts[0]}`
    } else {
      explanation = `£0 · No qualifying rates`
    }

    let typeLabel = 'Full day'

    if (parts.length > 0) {
      typeLabel = parts.join(' + ')
    } else if (isFirst) {
      typeLabel = 'Departure'
    } else if (isLast) {
      typeLabel = 'Return'
    }

    log.push({
      date: dayLabel(new Date(cur)),
      type: typeLabel,
      hours: Math.round(dayHrs * 10) / 10,
      amount: dayTotal,
      explanation,
    })

    cur.setDate(cur.getDate() + 1)
  }

  const grossTotal = total + breakfastDed

  const items: BreakdownItem[] = [
    {
      label: 'Gross meal allowance',
      description: `${log.length} day(s) · max 3 meal rates/day`,
      value: grossTotal,
      type: 'pos',
    },
  ]

  if (breakfastDed > 0) {
    items.push({
      label: 'Breakfast deductions',
      description: 'Provided by employer',
      value: -breakfastDed,
      type: 'neg',
    })
  }

  if (nights > 0) {
    items.push({
      label: 'Accommodation',
      description: 'Actual cost or bespoke rate agreed with HMRC',
      value: 0,
      type: 'zero',
    })
  }

  items.push({
    label: 'Total allowance',
    description: 'Tax-free (HMRC benchmark)',
    value: total,
    type: 'tot',
    isTotal: true,
  })

  return {
    currency: '£',
    total,
    items,
    log,
    ruleInfo: `<strong>UK HMRC Benchmark Scale Rates (EIM05231)</strong>: Each rate represents a meal. Max 3 meal rates per 24h (theoretical max £30). Rates: Breakfast £5 (early start before 6am), One meal £5 (5h+), Two meal £10 (10h+), Late evening £15 (at 8pm). Overnight days automatically qualify for the late evening rate. No receipt required. HMRC does not set a benchmark rate for accommodation.`,
    meta: `Domestic · ${Math.round(totalHrs)}h away · ${nights} overnight${nights === 1 ? '' : 's'}`,
  }
}

// ── Germany calculator ───────────────────────────────────────────

export const calcDE = (
  dep: Date,
  ret: Date,
  destMode: Destination,
  intlDest: string,
  meals: MealsState,
  priorDays: number,
): CalcResult => {
  if (destMode === 'international' && !intlDest) {
    return {currency: '€', total: 0, items: [], ruleInfo: 'Select a destination to calculate.', meta: ''}
  }

  const rates = intlDest ? GERMANY_INTERNATIONAL_RATES[intlDest] : {half: 14, full: 28}

  if (!rates) {
    return {currency: '€', total: 0, items: [], ruleInfo: 'Destination not found in BMF table.', meta: ''}
  }

  const FULL = rates.full
  const HALF = rates.half
  // BMF 2026: meal deductions are calculated off the destination's full-day rate,
  // not Germany's €28 domestic base (§9 Abs. 4a EStG; BMF-Schreiben 2025-12-05).
  const DEDUCT_BASE = FULL
  const log: DayLog[] = []
  let total = 0
  let dayNum = 0
  let cappedDays = 0
  const cur = new Date(dep)
  cur.setHours(0, 0, 0, 0)
  const last = new Date(ret)
  last.setHours(0, 0, 0, 0)

  while (cur <= last) {
    dayNum++
    const dayEnd = new Date(cur)
    dayEnd.setHours(23, 59, 59)
    const s = cur < dep ? dep : cur
    const e = dayEnd > ret ? ret : dayEnd
    const hrs = (e.getTime() - s.getTime()) / 3_600_000
    const isFirst = cur.toDateString() === dep.toDateString()
    const isLast = cur.toDateString() === last.toDateString()
    const limitHit = priorDays + dayNum > 90
    let type: string
    let base: number

    if (isFirst && isLast) {
      if (hrs >= 8) {
        base = HALF
        type = 'Single day (8h+ absence)'
      } else {
        base = 0
        type = 'Under 8h: no allowance'
      }
    } else if (isFirst || isLast) {
      base = HALF
      type = 'Arrival/departure (8h+ rate)'
    } else {
      base = FULL
      type = 'Full day (24h+ absence)'
    }

    if (limitHit) {
      base = 0
      type += ' (Dreimonatsfrist cap)'
      cappedDays++
    }

    let ded = 0

    if (base > 0) {
      if (meals.breakfast) {
        ded += DEDUCT_BASE * 0.2
      }

      if (meals.lunch) {
        ded += DEDUCT_BASE * 0.4
      }

      if (meals.dinner) {
        ded += DEDUCT_BASE * 0.4
      }

      ded = Math.min(ded, base)
    }

    const amt = Math.max(0, base - ded)
    total += amt

    let deExplanation: string

    if (limitHit) {
      deExplanation = `€0 · 3-month cap reached`
    } else if (base === 0) {
      deExplanation = `€0 · Under 8h absence`
    } else if (ded > 0) {
      const dedParts: string[] = []

      if (meals.breakfast) {
        dedParts.push('20% breakfast')
      }

      if (meals.lunch) {
        dedParts.push('40% lunch')
      }

      if (meals.dinner) {
        dedParts.push('40% dinner')
      }

      deExplanation = `€${base} − €${ded.toFixed(2)} (${dedParts.join(
        ' + ',
      )} off €${DEDUCT_BASE} base) = €${amt.toFixed(2)}`
    } else {
      const rateLabel = base === FULL ? 'Full-day rate' : 'Half-day rate'
      deExplanation = `€${base} · ${rateLabel}`
    }

    log.push({
      date: dayLabel(new Date(cur)),
      type,
      hours: Math.round(hrs * 10) / 10,
      amount: amt,
      explanation: deExplanation,
    })

    cur.setDate(cur.getDate() + 1)
  }

  const gross = log.reduce((s, d) => s + d.amount, 0)

  const totalDed = log.reduce((s, d) => {
    if (d.type.includes('no allowance') || d.type.includes('cap')) {
      return s
    }

    const base = d.type.includes('Full day') ? FULL : HALF
    const rawDed = DEDUCT_BASE * ((meals.breakfast ? 0.2 : 0) + (meals.lunch ? 0.4 : 0) + (meals.dinner ? 0.4 : 0))

    return s + Math.min(rawDed, base)
  }, 0)

  const items: BreakdownItem[] = [
    {
      label: 'Gross meal allowance',
      description: `${log.length} day(s) · ${intlDest || 'Germany (domestic)'}`,
      value: gross + totalDed,
      type: 'pos',
    },
  ]

  if (totalDed > 0) {
    items.push({
      label: 'Meal deductions',
      description: `Employer meals (off €${DEDUCT_BASE} full-day rate)`,
      value: -totalDed,
      type: 'neg',
    })
  }

  if (priorDays >= 90) {
    items.push({
      label: 'Three-month cap reached',
      description: `${priorDays} prior days logged (Dreimonatsfrist)`,
      value: 0,
      type: 'zero',
    })
  } else if (cappedDays > 0) {
    items.push({
      label: 'Three-month cap partially applied',
      description: `${cappedDays} day(s) capped (prior ${priorDays} + trip crosses 90-day Dreimonatsfrist)`,
      value: 0,
      type: 'zero',
    })
  }

  items.push({label: 'Net meal allowance', description: 'Tax-free', value: total, type: 'tot', isTotal: true})
  const destNote = intlDest ? `${intlDest}: €${HALF}/€${FULL} (BMF 2026)` : 'Domestic: €14/€28'
  const rateNote = intlDest ? `BMF 2026: ${intlDest} · €${HALF} (8h+) / €${FULL} (24h)` : undefined

  return {
    currency: '€',
    total,
    items,
    log,
    ruleInfo: `<strong>German BMF Rates (2026)</strong>: ${destNote}. The half-day rate applies to absences of 8+ hours (including arrival/departure days of multi-day trips). The full-day rate applies to full-day trips with 24+ hours of absence. Meal deductions: 20% breakfast, 40% lunch/dinner, calculated off the <strong>full-day rate for the destination</strong> (€${DEDUCT_BASE} here) per §9 Abs. 4a EStG. Tax-free limit: <strong>three months (Dreimonatsfrist)</strong> at same location; resets after 4-week break.`,
    meta: `${intlDest || 'Domestic'} · ${log.length} day(s) · ${
      priorDays > 0 ? priorDays + ' prior days' : 'First visit'
    }`,
    rateNote,
  }
}

// ── Austria calculator ───────────────────────────────────────────

export const calcAT = (
  dep: Date,
  ret: Date,
  destMode: Destination,
  intlDest: string,
  meals: MealsState,
  distance: number,
): CalcResult => {
  if (distance < 25) {
    return {
      currency: '€',
      total: 0,
      items: [],
      ruleInfo: '<strong>Austrian Rules</strong>: Destination must be ≥25 km from workplace.',
      meta: 'Not eligible: under 25 km',
      warn: 'Destination is under 25 km from workplace. No per diem applies.',
    }
  }

  const totalHrs = (ret.getTime() - dep.getTime()) / 3_600_000

  if (totalHrs < 3) {
    return {
      currency: '€',
      total: 0,
      items: [],
      ruleInfo: '<strong>Austrian Rules</strong>: Minimum trip duration is 3 hours.',
      meta: 'Not eligible: under 3 hours',
      warn: 'Trip is under 3 hours. Minimum duration required.',
    }
  }

  if (destMode === 'international' && !intlDest) {
    return {currency: '€', total: 0, items: [], ruleInfo: 'Select a destination to calculate.', meta: ''}
  }

  const FULL = intlDest ? AUSTRIA_INTERNATIONAL_RATES[intlDest] || 40 : 30
  const HOURLY_RATE = FULL / 12
  const log: DayLog[] = []
  let total = 0
  let dayNum = 0
  const cur = new Date(dep)
  cur.setHours(0, 0, 0, 0)
  const last = new Date(ret)
  last.setHours(0, 0, 0, 0)

  while (cur <= last) {
    dayNum++
    const dayEnd = new Date(cur)
    dayEnd.setHours(23, 59, 59)
    const s = cur < dep ? dep : cur
    const e = dayEnd > ret ? ret : dayEnd
    const hrs = (e.getTime() - s.getTime()) / 3_600_000
    const ch = Math.ceil(hrs)
    // 15-day Einsatzort rule: after 15 days at the same domestic location, tagged as "Mittelpunkt der
    // Tätigkeit" and per diem becomes taxable. The 120 km rule is a separate Nächtigungsgeld provision
    // (overnight taxability after 6 months when daily return is unreasonable), not a gate on this rule.
    const limitHit = dayNum > 15 && !intlDest
    let base = 0
    let type = ''

    if (ch <= 3) {
      type = '≤3h: no allowance'
      base = 0
    } else if (ch >= 12) {
      type = `12h+: full day (€${FULL})`
      base = FULL
    } else {
      base = Math.round(ch * HOURLY_RATE * 100) / 100
      type = `${ch}h × €${HOURLY_RATE.toFixed(2)}`
    }

    if (limitHit) {
      base = 0
      type += ' (15-day domestic limit)'
    }

    // Meal deductions: flat €FULL/2 per meal (not percentage of prorated base)
    const MEAL_DED = FULL / 2
    let ded = 0

    if (base > 0) {
      if (meals.lunch) {
        ded += MEAL_DED
      }

      if (meals.dinner) {
        ded += MEAL_DED
      }

      ded = Math.min(ded, base)
    }

    const amt = Math.max(0, base - ded)
    total += amt

    let atExplanation: string
    const dedParts: string[] = []

    if (meals.lunch) {
      dedParts.push(`€${MEAL_DED.toFixed(2)} lunch`)
    }

    if (meals.dinner) {
      dedParts.push(`€${MEAL_DED.toFixed(2)} dinner`)
    }

    if (limitHit) {
      atExplanation = `€0 · 15-day domestic limit`
    } else if (ch <= 3) {
      atExplanation = `€0 · Under 3h absence`
    } else if (ded > 0) {
      atExplanation = `€${base.toFixed(2)} − ${dedParts.join(' + ')} = €${amt.toFixed(2)}`
    } else if (ch >= 12) {
      atExplanation = `€${FULL} · Full-day rate`
    } else {
      atExplanation = `${ch} × €${HOURLY_RATE.toFixed(2)} = €${base.toFixed(2)} · Hourly calculation`
    }

    log.push({
      date: dayLabel(new Date(cur)),
      type,
      hours: Math.round(hrs * 10) / 10,
      amount: amt,
      explanation: atExplanation,
    })

    cur.setDate(cur.getDate() + 1)
  }

  const nights = Math.max(0, log.length - 1)
  const overnightTotal = nights * 17
  total += overnightTotal
  const gross = log.reduce((s, d) => s + d.amount, 0)

  const items: BreakdownItem[] = [
    {
      label: 'Meal allowance (gross)',
      description: `Hourly calc · ${intlDest || 'domestic'}`,
      value: gross,
      type: 'pos',
    },
  ]

  if (nights > 0) {
    items.push({
      label: 'Overnight allowance',
      description: `${nights} night(s) × €17`,
      value: overnightTotal,
      type: 'pos',
    })
  }

  items.push({label: 'Total allowance', description: 'Tax-free', value: total, type: 'tot', isTotal: true})
  const rateNote = intlDest ? `Austria → ${intlDest} · €${FULL}/day (BMF Lohnsteuerrichtlinien Rz 1405)` : undefined

  return {
    currency: '€',
    total,
    items,
    log,
    ruleInfo: `<strong>Austrian BMF Lohnsteuerrichtlinien (2026)</strong>: ${
      intlDest ? `${intlDest}: €${FULL}/day base (Rz 1405, foreign travel rates). ` : 'Domestic: €30/day, €17/night. '
    }Full-day rate ÷ 12 per commenced hour; max at 12h+. Overnight: €17/night. Meal deductions: €${(FULL / 2).toFixed(
      2,
    )} flat per lunch or dinner provided (half of full-day rate).`,
    meta: `${intlDest || 'Domestic'} · ${log.length} day(s) · ${nights} night(s) · ${distance} km`,
    rateNote,
  }
}

// ── Netherlands calculator ───────────────────────────────────────

export const calcNL = (
  dep: Date,
  ret: Date,
  destMode: Destination,
  intlDest: string,
  meals: MealsState,
  municipality: string,
): CalcResult => {
  if (municipality === 'no') {
    return {
      currency: '€',
      total: 0,
      items: [],
      ruleInfo: '<strong>Dutch WKR Rules</strong>: Trip must be to a different municipality or ≥1 km from work.',
      meta: 'Not eligible',
      warn: 'Destination must be in a different municipality or ≥1 km from work.',
    }
  }

  const hrs = (ret.getTime() - dep.getTime()) / 3_600_000

  if (hrs < 4) {
    return {
      currency: '€',
      total: 0,
      items: [],
      ruleInfo: '<strong>Dutch WKR Rules</strong>: Minimum trip duration is 4 hours.',
      meta: 'Not eligible: under 4 hours',
      warn: 'Trip must last at least 4 hours.',
    }
  }

  if (destMode === 'international' && !intlDest) {
    return {currency: '€', total: 0, items: [], ruleInfo: 'Select a destination to calculate.', meta: ''}
  }

  const isIntl = destMode === 'international'
  const COUNTRY = isIntl ? NETHERLANDS_INTERNATIONAL_RATES[intlDest] || 52 : 39.33
  const items: BreakdownItem[] = []
  let total = 0
  const dh = dep.getHours()
  const rh = ret.getHours()
  const nights = Math.max(0, Math.floor((ret.getTime() - dep.getTime()) / 86_400_000))

  if (isIntl) {
    // International: hourly incidentals at 1.5% of country rate for the full absence duration
    const hoursAllo = Math.round(hrs * (COUNTRY * 0.015) * 100) / 100
    const BF = Math.round(COUNTRY * 0.12 * 100) / 100
    const LU = Math.round(COUNTRY * 0.2 * 100) / 100
    const DI = Math.round(COUNTRY * 0.32 * 100) / 100

    items.push({
      label: 'Hourly incidentals',
      description: `${Math.round(hrs)}h × 1.5% of €${COUNTRY}`,
      value: hoursAllo,
      type: 'pos',
    })

    total += hoursAllo

    // Meal eligibility: trip must span the meal window
    const spansBf = dh < 8 // Departure before 08:00 (breakfast window 06:00–08:00)
    const spansLu = dh < 14 && rh >= 12 // Trip spans any part of 12:00–14:00
    const spansDi = rh >= 18 // Return after 18:00 (dinner window 18:00–21:00)

    if (!meals.breakfast && spansBf) {
      items.push({label: 'Breakfast allowance', description: `06:00–08:00 · €${BF}`, value: BF, type: 'pos'})
      total += BF
    } else if (meals.breakfast && spansBf) {
      items.push({label: 'Breakfast', description: 'Provided: deducted', value: -BF, type: 'neg'})
      total -= BF
    }

    if (!meals.lunch && spansLu) {
      items.push({label: 'Lunch allowance', description: `12:00–14:00 · €${LU}`, value: LU, type: 'pos'})
      total += LU
    } else if (meals.lunch && spansLu) {
      items.push({label: 'Lunch', description: 'Provided: deducted', value: -LU, type: 'neg'})
      total -= LU
    }

    if (!meals.dinner && spansDi) {
      items.push({label: 'Dinner allowance', description: `18:00–21:00 · €${DI}`, value: DI, type: 'pos'})
      total += DI
    } else if (meals.dinner && spansDi) {
      items.push({label: 'Dinner', description: 'Provided: deducted', value: -DI, type: 'neg'})
      total -= DI
    }
  } else {
    // Domestic: CAO Rijk "kleine uitgaven" (small incidentals — not meal rates).
    // Awarded once per trip when the respective daytime/evening window is spanned.
    // Known simplification: CAO Rijk specifies per-day amounts; multi-day trips under-award here.
    const SMALL_DAY = 7.35
    const SMALL_EVENING = 21.92
    const spansDaytime = dh < 18
    const spansEvening = rh >= 18

    if (spansDaytime) {
      items.push({
        label: 'Small expenses (daytime)',
        description: 'Kleine uitgaven overdag · CAO Rijk',
        value: SMALL_DAY,
        type: 'pos',
      })

      total += SMALL_DAY
    }

    if (spansEvening) {
      items.push({
        label: 'Small expenses (evening)',
        description: 'Kleine uitgaven \u2019s avonds · CAO Rijk',
        value: SMALL_EVENING,
        type: 'pos',
      })

      total += SMALL_EVENING
    }
  }

  if (nights > 0) {
    if (isIntl) {
      const EVE = 7.09
      const full = Math.min(nights, 8)
      const half = Math.max(0, nights - 8)
      const eveTotal = full * EVE + half * (EVE / 2)

      items.push({
        label: 'Evening allowance',
        description: nights > 8 ? `8 × €${EVE} + ${half} × €${(EVE / 2).toFixed(2)}` : `${nights} × €${EVE}`,
        value: eveTotal,
        type: 'pos',
      })

      total += eveTotal

      items.push({
        label: 'Accommodation',
        description: 'Invoice required for international stays',
        value: 0,
        type: 'zero',
      })
    } else {
      items.push({
        label: 'Accommodation',
        description: 'Actual cost (receipt required) · up to CAO Rijk maximum',
        value: 0,
        type: 'zero',
      })
    }
  }

  total = Math.round(total * 100) / 100

  items.push({
    label: 'Total allowance',
    description: 'Tax-free · CAO Rijk basis',
    value: total,
    type: 'tot',
    isTotal: true,
  })

  const rateNote = isIntl ? `CAO Rijk 2026: ${intlDest} · base rate €${COUNTRY}/day` : undefined

  return {
    currency: '€',
    total,
    items,
    ruleInfo: `<strong>Dutch WKR / CAO Rijk (2026)</strong>: ${
      isIntl
        ? `${intlDest} (CAO Rijk base: €${COUNTRY}). Incidentals: 1.5%/h of country rate. Meals apply within standard time windows.`
        : 'Domestic Netherlands. CAO Rijk "kleine uitgaven": €7.35/day daytime incidentals, €21.92/day evening incidentals. These are <em>small expenses</em>, not meal rates. If meals are purchased with receipts, CAO Rijk meal maxima (€16.07 breakfast, €22.19 lunch, €33.57 dinner) apply separately and are reimbursed at actual cost. Accommodation reimbursed at actual cost up to CAO Rijk maximum.'
    } ${
      isIntl ? 'Evening allowance halves from night 9. ' : ''
    }CAO Rijk rates updated annually on 1 January.<br><br><strong>Note:</strong> Unlike Germany and Austria, the Netherlands has no fixed statutory per diem. These amounts follow the CAO Rijk (Collective Labour Agreement for government employees), widely used as a reference by Dutch employers but not binding. Actual entitlements depend on company or municipal policy.`,
    meta: `${intlDest || 'Domestic'} · ${Math.round(hrs)}h · ${nights} overnight(s)`,
    rateNote,
  }
}
